import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";

type Props = { connectionId: string; exchange: string };

export function GateBatchControl({ connectionId, exchange }: Props) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const url = `https://lukkypro.ru/api/v1/bots/${connectionId}/gate-batch`;
  const request = useCallback(async (next?: boolean) => {
    const response = await fetch(url, {
      method: next === undefined ? "GET" : "PUT",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "Content-Type": "application/json",
      },
      body: next === undefined ? undefined : JSON.stringify({ enabled: next }),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.detail || result.message || "Ошибка сохранения batch-режима");
    if (typeof result.enabled !== "boolean") throw new Error("Не удалось подтвердить состояние batch-режима");
    return result.enabled as boolean;
  }, [url]);

  useEffect(() => {
    if (exchange !== "GATE") return;
    let active = true;
    setEnabled(null);
    request().then(value => {
      if (active) { setEnabled(value); setError(""); }
    }).catch(() => {
      if (active) setError("Не удалось загрузить состояние batch-режима. Обновите состояние.");
    });
    return () => { active = false; };
  }, [exchange, request]);

  const change = async (next?: boolean) => {
    setBusy(true);
    setError("");
    try {
      setEnabled(await request(next));
    } catch {
      setEnabled(null);
      setError("Не удалось подтвердить состояние batch-режима. Обновите состояние.");
    } finally {
      setBusy(false);
    }
  };

  if (exchange !== "GATE") return null;
  return <Box sx={{ p: 2, my: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
    <FormControlLabel
      control={<Checkbox checked={enabled === true} disabled={busy || enabled === null}
        onChange={event => change(event.target.checked)} />}
      label="Тестовый режим: BUY и SELL одним batch"
    />
    <Typography variant="body2" color="text.secondary">
      Реальные ордера. Каждая пара отправляется одним запросом Gate. Снимите галочку,
      чтобы со следующей пары вернуться к двум отдельным запросам. Настройка сохраняется сразу.
    </Typography>
    <Typography variant="caption" role="status">
      {busy ? "Сохраняем…" : enabled === null ? "Состояние не подтверждено" : enabled ? "Batch включён" : "Batch выключен"}
    </Typography>
    {error && <Alert severity="error" sx={{ mt: 1 }}>
      {error} <Button disabled={busy} onClick={() => change()}>Обновить состояние</Button>
    </Alert>}
  </Box>;
}
